import React, { useState, useEffect } from "react";
import {
  MessageSquare,
  Mail,
  Save as SaveIcon,
  Check,
  Zap,
} from "lucide-react";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";
import { ResellingSkeletonLoader } from "./components/ResellingSkeletonLoader";

const ICON_MAP: Record<string, any> = {
  sms: MessageSquare,
  email: Mail,
};

const Reselling = () => {
  const { user } = useUserInfo();
  const AppService = useAppServices();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState<any[]>([]);
  const [resellings, setResellings] = useState<any[]>([]);

  const getServices = async () => {
    try {
      const { response } = await AppService.service.filter({});
      setServices(response?.data || []);
    } catch (error) {
      console.error("Error loading services:", error);
      setServices([]);
    }
  };

  const getResellings = async () => {
    try {
      const { response } = await AppService.reselling.filter({
        query: `companyId=${user?.companyId}`,
      });
      setResellings(response?.data || []);
    } catch {
      setResellings([]);
    }
  };
  const onLoad = async () => {
    setLoading(true);
    await Promise.all([getServices(), getResellings()]);
    setLoading(false);
  };
  let pageLoad = true;
  useEffect(() => {
    if (pageLoad) {
      onLoad();
      pageLoad = false;
    }
  }, []);

  const getResellingConfig = (serviceId: string) =>
    resellings.find((r) => r.serviceId === serviceId);

  const handleSave = async (service: any, config: any) => {
    try {
      const payload = {
        pricingType: config.pricingType,
        isActive: config.isActive,
        ...(config.pricingType === "custom" && {
          customPrice: config.customPrice,
        }),
        ...(config.pricingType === "multiplier" && {
          multiplier: config.multiplier,
        }),
      };

      if (config?._id) {
        await AppService.reselling.update({
          payload: {
            ...payload,
            _id: config?._id,
          },
          toaster: true,
        });
      } else {
        await AppService.reselling.create({
          payload: {
            ...payload,
            companyId: user.companyId,
            serviceId: service._id,
          },
          toaster: true,
        });
      }
      await getResellings();
    } catch (error: any) {}
  };

  return (
    <div className="card p-4 sm:p-6">
      <div className="">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Reselling Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Configure your pricing and profit margins for SMS and Email
              services
            </p>
          </div>
        </div>
        {loading ? (
          <ResellingSkeletonLoader />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {services.map((service) => {
              const config = getResellingConfig(service._id) || {
                pricingType: "default",
                isActive: service.isActive,
                customPrice: service.basePrice,
                multiplier: 1,
              };

              const Icon = ICON_MAP[service.key] || MessageSquare;
              const base = service.basePrice;
              const effectivePrice =
                config.pricingType === "multiplier"
                  ? base * (config.multiplier || 1)
                  : config.customPrice || base;
              const profit = +(effectivePrice - base).toFixed(2);
              const profitPercentage = +((profit / base) * 100).toFixed(1);

              const updateField = (patch: any) => {
                setResellings((prev) => {
                  const exists = prev.find((r) => r.serviceId === service._id);
                  if (exists) {
                    return prev.map((r) =>
                      r.serviceId === service._id ? { ...r, ...patch } : r
                    );
                  }
                  return [...prev, { serviceId: service._id, ...patch }];
                });
              };

              return (
                <div
                  key={service._id}
                  className={`bg-white dark:bg-gray-800 rounded-xl border-2 transition-all duration-200 ${
                    service.isResellable
                      ? "border-blue-100 dark:border-blue-900/30 shadow-sm hover:shadow-md hover:translate-y-[-2px]"
                      : "border-gray-100 dark:border-gray-700 opacity-60"
                  }`}
                >
                  <div className="p-6 pb-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3 flex-1">
                        <div
                          className={`p-3 rounded-xl transition-colors ${
                            service.isResellable
                              ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-400"
                          }`}
                        >
                          <Icon size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 dark:text-white truncate text-lg">
                            {service.name}
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 line-clamp-2">
                            {service.description || "No description available"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Base Price
                      </span>
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">
                        ${base.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="px-6 pb-6 space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Pricing Strategy
                      </label>
                      <select
                        disabled={!service.isResellable}
                        value={config.pricingType}
                        onChange={(e) =>
                          updateField({ pricingType: e.target.value })
                        }
                        className={`w-full border rounded-lg px-3 py-2.5 text-sm transition-colors
            ${
              service.isResellable
                ? "border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                : "border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
            }`}
                      >
                        <option value="default">Default Pricing</option>
                        <option value="custom">Custom Price</option>
                        <option value="multiplier">Multiplier</option>
                      </select>
                    </div>

                    {config.pricingType === "multiplier" && (
                      <div
                        className={`bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800 ${
                          !service.isResellable
                            ? "opacity-50 pointer-events-none"
                            : ""
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Price Multiplier
                          </label>
                          <span className="font-bold text-blue-600 dark:text-blue-400 text-lg">
                            {config.multiplier}x
                          </span>
                        </div>
                        <input
                          type="range"
                          min={1}
                          max={10}
                          step={0.1}
                          disabled={!service.isResellable}
                          value={config.multiplier}
                          onChange={(e) =>
                            updateField({ multiplier: Number(e.target.value) })
                          }
                          className="w-full h-2 bg-blue-200 dark:bg-blue-800 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                    )}

                    {config.pricingType === "custom" && (
                      <div
                        className={
                          !service.isResellable
                            ? "opacity-50 pointer-events-none"
                            : ""
                        }
                      >
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Your Price
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">
                            $
                          </span>
                          <input
                            type="number"
                            disabled={!service.isResellable}
                            value={config.customPrice}
                            onChange={(e) =>
                              updateField({
                                customPrice: Number(e.target.value),
                              })
                            }
                            className="w-full pl-8 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Your Price
                        </p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          ${effectivePrice.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Profit
                        </p>
                        <p
                          className={`text-lg font-bold ${
                            profit >= 0
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          ${profit.toFixed(2)}
                          <span className="text-xs ml-1">
                            ({profitPercentage}%)
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30 rounded-b-2xl border-t border-gray-100 dark:border-gray-600">
                    <button
                      onClick={() => handleSave(service, config)}
                      disabled={!service.isResellable}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-base font-semibold transition-all ${
                        service.isResellable
                          ? "bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md"
                          : "bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      <SaveIcon size={18} />
                      Save Changes
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {services.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <MessageSquare className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No Services Available
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              There are no services configured yet. Services will appear here
              once they are added.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reselling;
